/**
 * `useReadReceipt` — Observe message visibility and report read state.
 *
 * Responsibilities (design.md §Frontend Layer › useReadReceipt,
 * Requirements R6.2 / R6.5):
 *   - Register message DOM elements via `observe(el, seq)` so the hook can
 *     track which seqs are currently on screen.
 *   - Use `IntersectionObserver` (created once per mount) to detect when a
 *     message becomes visible (threshold 0.5 — the design says "expressed
 *     in the UI as 'read' once at least half is on screen").
 *   - Maintain a debounced report channel: every time a higher seq becomes
 *     visible, restart a `debounceMs` (default 500ms) timer. When the
 *     timer fires, POST `chatApi.markRead(roomId, { nickname, lastReadSeq })`
 *     with the maximum seq observed so far.
 *   - Skip POSTs whose lastReadSeq is not strictly greater than the last
 *     successfully reported seq (no duplicate writes; the server is also
 *     monotonic but the client is the first line of defense).
 *   - On `markRead` failure, log a `console.warn` and leave
 *     `lastReportedSeq` unchanged so the next observation can retry.
 *   - Clean up on unmount: disconnect the observer, clear any pending
 *     debounce, and refuse to advance `lastReportedSeq` from a late-arriving
 *     resolved Promise.
 *
 * Dependency direction (design.md §File Structure Plan):
 *   - This hook depends only on `react` and the `ChatApi` type from
 *     `../api/chatApi`. No coupling to SignalR or UI components.
 *
 * Notes on debounce semantics:
 *   - "Sliding" debounce: each new high-water-mark seq resets the timer.
 *     This matches the spec's intent ("500ms debounce で markRead") and
 *     means a fast scroll fires only once, with the seq the user landed on.
 *   - A seq that's <= lastReportedSeq is silently dropped without
 *     scheduling — there's nothing to report.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { ChatApi } from '../api/chatApi';

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

/** Options accepted by `useReadReceipt`. */
export interface UseReadReceiptOptions {
  /** REST client used for `markRead`. */
  readonly chatApi: ChatApi;
  /** The room whose messages we're tracking. */
  readonly roomId: string;
  /** Caller's own nickname — used as the markRead key. */
  readonly myNickname: string;
  /**
   * Debounce window in milliseconds. Defaults to 500 (design.md). Lower
   * values are useful for tests; higher values reduce write volume on
   * slow connections.
   */
  readonly debounceMs?: number;
}

/** Result shape returned from `useReadReceipt`. */
export interface UseReadReceiptResult {
  /**
   * Register a message element + its seq with the observer. Returns a
   * teardown function the caller MUST invoke when the element is removed
   * from the DOM (or to stop observing for any reason).
   */
  readonly observe: (messageEl: HTMLElement, seq: number) => () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_DEBOUNCE_MS = 500;

/** Threshold for "visible" — at least half the element on-screen. */
const VISIBILITY_THRESHOLD = 0.5;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useReadReceipt(
  options: UseReadReceiptOptions,
): UseReadReceiptResult {
  const { chatApi, roomId, myNickname, debounceMs = DEFAULT_DEBOUNCE_MS } =
    options;

  // -- Refs --------------------------------------------------------------
  // Element → seq map (stable across renders).
  const elToSeqRef = useRef<Map<Element, number>>(new Map());

  // Highest seq that has become visible since the hook mounted.
  const latestVisibleSeqRef = useRef<number>(0);

  // Last seq successfully reported to the server. Only advances on a
  // successful `markRead` response.
  const lastReportedSeqRef = useRef<number>(0);

  // Pending debounce timer id (`number` in browsers, `NodeJS.Timeout` in
  // node — we keep it loosely-typed because we only need to clearTimeout).
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Guard against post-unmount work (timer callbacks, late Promise
  // resolutions).
  const unmountedRef = useRef<boolean>(false);

  // The IntersectionObserver instance. Held in a ref so it survives
  // re-renders without being re-created.
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Keep latest config reachable from the IO callback without forcing a
  // re-creation of the observer when these change.
  const chatApiRef = useRef<ChatApi>(chatApi);
  chatApiRef.current = chatApi;
  const roomIdRef = useRef<string>(roomId);
  roomIdRef.current = roomId;
  const nicknameRef = useRef<string>(myNickname);
  nicknameRef.current = myNickname;
  const debounceMsRef = useRef<number>(debounceMs);
  debounceMsRef.current = debounceMs;

  // -- Report function (debounced trigger target) ------------------------
  // Declared as a stable ref-bound function so the timer callback can call
  // it without being in the React render path.
  const flushMarkRead = useCallback(async () => {
    debounceTimerRef.current = null;

    if (unmountedRef.current) return;

    const candidate = latestVisibleSeqRef.current;
    if (candidate <= lastReportedSeqRef.current) {
      // Nothing new to report — the latest visible seq has already been
      // (or is being) reported. This guard prevents a redundant POST
      // when the debounce was scheduled by a transient observation that
      // was later superseded by a smaller-or-equal-seq state.
      return;
    }

    // Snapshot the value we're about to commit to. If a higher seq becomes
    // visible while the request is in flight, the next observation will
    // see that lastReportedSeqRef hasn't moved forward past `candidate`
    // yet and will schedule its own POST after the current one settles.
    const target = candidate;

    try {
      const result = await chatApiRef.current.markRead(roomIdRef.current, {
        nickname: nicknameRef.current,
        lastReadSeq: target,
      });

      if (unmountedRef.current) return;

      if (result.ok) {
        // Only advance on success. Use max() in case a parallel flow has
        // already pushed the watermark further (shouldn't happen with
        // the current single-timer design, but defensive).
        if (target > lastReportedSeqRef.current) {
          lastReportedSeqRef.current = target;
        }
      } else {
        // Failure path: leave lastReportedSeqRef alone so the next
        // observation can retry. Log for diagnostics — the UI doesn't
        // need to surface this because read receipts are best-effort.
        console.warn('useReadReceipt: markRead failed', result.error);
      }
    } catch (cause) {
      // The ChatApi contract converts network errors into `{ ok: false }`,
      // so this catch is purely defensive against pathological mocks /
      // future changes. Same treatment as a failed result.
      if (unmountedRef.current) return;
      console.warn('useReadReceipt: markRead threw', cause);
    }
  }, []);

  // -- IntersectionObserver lifecycle -----------------------------------
  // Created once on mount. Re-creating on prop changes would lose the
  // currently-observed elements. The callback reads everything it needs
  // through refs, so it stays valid across `chatApi` / `roomId` etc.
  // changes.
  useEffect(() => {
    unmountedRef.current = false;

    const observer = new IntersectionObserver(
      (entries) => {
        if (unmountedRef.current) return;

        // Compute the largest seq among entries that just became visible.
        // We DON'T fold in `latestVisibleSeqRef.current` here — that ref
        // is the running watermark of "ever-seen", not "currently visible
        // in this batch". The batch-local max is what tells us whether
        // this notification carries new information.
        let batchMax = 0;
        let sawVisible = false;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const seq = elToSeqRef.current.get(entry.target);
          if (seq === undefined) continue;
          sawVisible = true;
          if (seq > batchMax) batchMax = seq;
        }

        if (!sawVisible) return;

        // Promote the watermark monotonically.
        if (batchMax > latestVisibleSeqRef.current) {
          latestVisibleSeqRef.current = batchMax;
        }

        // Schedule a debounced report whenever the current watermark
        // exceeds what we've successfully sent. This intentionally
        // includes the case where the watermark hasn't moved since the
        // last batch — that's the "retry after failed POST" path: the
        // same seq becomes visible again, lastReportedSeqRef is still
        // behind, so we try again.
        if (latestVisibleSeqRef.current <= lastReportedSeqRef.current) return;

        if (debounceTimerRef.current !== null) {
          clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
          // setTimeout swallows the returned promise; the body of
          // flushMarkRead is responsible for its own error handling.
          void flushMarkRead();
        }, debounceMsRef.current);
      },
      { threshold: VISIBILITY_THRESHOLD },
    );

    observerRef.current = observer;

    return () => {
      unmountedRef.current = true;
      observer.disconnect();
      observerRef.current = null;
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      elToSeqRef.current.clear();
    };
  }, [flushMarkRead]);

  // -- Public observe() --------------------------------------------------
  // The returned teardown is intentionally not memoized per (el, seq) —
  // callers pair each `observe()` with its own teardown, so identity
  // doesn't matter for hook-internal correctness.
  const observe = useCallback((messageEl: HTMLElement, seq: number) => {
    const observer = observerRef.current;
    if (observer === null) {
      // Hook was unmounted before the caller invoked observe(). Return a
      // no-op teardown so the caller's code paths stay symmetric.
      return () => undefined;
    }

    elToSeqRef.current.set(messageEl, seq);
    observer.observe(messageEl);

    return () => {
      // Tear down: unobserve and forget the seq mapping. Safe to call
      // multiple times because `Map.delete` and `IO.unobserve` are both
      // idempotent for absent keys / elements.
      const obs = observerRef.current;
      if (obs !== null) obs.unobserve(messageEl);
      elToSeqRef.current.delete(messageEl);
    };
  }, []);

  // -- Result ------------------------------------------------------------
  // Memoize the result object so consumers can use it as a stable
  // dependency in their own effects.
  return useMemo<UseReadReceiptResult>(() => ({ observe }), [observe]);
}
